/**
 * Chargement & stockage des labels Gmail. Trois catégories :
 *
 *  - **Splits** : labels qu'on affiche comme filter pills en haut. Préfixe
 *    `[NaoMail]/` (convention Mailfox actuelle) ou `_` (legacy de
 *    l'ancienne config Superhuman, support gardé pour transition).
 *    Affichage : préfixe strippé (`[NaoMail]/FYI` → `FYI`, `_FYI` → `FYI`).
 *
 *  - **Chips** : labels "normaux" qu'on affiche en bas de chaque row inbox
 *    pour identifier le contenu (e.g. "shipping", "marketing"). Inclut
 *    tous les labels user qui ne sont NI des splits NI bracket-préfixés.
 *
 *  - **Hidden** : labels qui appartiennent à d'autres outils (Superhuman,
 *    Notion, etc.) — on détecte par le pattern `[<n'importe quoi>]` au
 *    début du nom, sauf `[NaoMail]/...` qu'on traite comme un split.
 *    Inclut aussi les sous-labels (`[Notion]/IA`, etc.).
 *
 * Le chargement est paresseux : déclenché par `loadLabels()` au boot
 * une fois signed-in. Re-chargement manuel possible via `loadLabels(true)`.
 */

import { listLabels, type GmailLabel, type GmailMessage } from "./api";

interface LabelsState {
  /** Tous les labels (system + user). */
  all: GmailLabel[];
  /** Labels avec préfixe `_` — utilisés comme tabs en bas. */
  splits: GmailLabel[];
  /** Map id → label pour résolution O(1) côté chips de rows. */
  byId: Map<string, GmailLabel>;
  loading: boolean;
  loaded: boolean;
  error: string | null;
}

export const labelsState = $state<LabelsState>({
  all: [],
  splits: [],
  byId: new Map(),
  loading: false,
  loaded: false,
  error: null,
});

/**
 * Préfixes que l'app considère comme des "splits" (tab pills). Seuls
 * les labels `[NaoMail]/...` apparaissent comme splits. Les `_*` legacy
 * ne sont plus traités spécialement (tombent dans la branche "labels
 * normaux", donc affichés comme chips inline si non-bracket-prefixed).
 */
const SPLIT_PREFIXES = ["[NaoMail]/"] as const;

/** True si le nom commence par un de nos préfixes split. */
function isSplitName(name: string): boolean {
  return SPLIT_PREFIXES.some((p) => name.startsWith(p));
}

/**
 * True si le nom commence par `[xxx]` (label propriété d'un autre outil :
 * Superhuman, Notion, etc.). Ces labels sont entièrement masqués —
 * incluant leurs sous-labels (`[Notion]/IA`).
 */
function isBracketPrefixed(name: string): boolean {
  return /^\[[^\]]+\]/.test(name);
}

/** Charge les labels une seule fois (idempotent). */
export async function loadLabels(force = false): Promise<void> {
  if (labelsState.loading) return;
  if (labelsState.loaded && !force) return;
  labelsState.loading = true;
  labelsState.error = null;
  try {
    const all = await listLabels();
    labelsState.all = all;
    labelsState.splits = all
      .filter((l) => l.type === "user" && isSplitName(l.name))
      // Tri alphabétique — laisse à l'user le contrôle de l'ordre via le
      // nom (`[NaoMail]/1 FYI`, `[NaoMail]/2 To Respond`, etc. si besoin).
      .sort((a, b) => a.name.localeCompare(b.name));
    labelsState.byId = new Map(all.map((l) => [l.id, l]));
    labelsState.loaded = true;
  } catch (e) {
    labelsState.error = e instanceof Error ? e.message : String(e);
  } finally {
    labelsState.loading = false;
  }
}

/** Affichage human-readable d'un label : strip les préfixes split. */
export function displayLabel(name: string): string {
  for (const p of SPLIT_PREFIXES) {
    if (name.startsWith(p)) return name.slice(p.length);
  }
  return name;
}

/**
 * Calcule les chips à afficher sur une row inbox pour un thread donné.
 *
 *  - Union des `labelIds` de tous les messages du thread, dédup
 *  - Filtre `type === "user"` (exclut INBOX/UNREAD/SENT/IMPORTANT/etc.)
 *  - Exclut les splits (`[NaoMail]/*`, `_*`) — déjà visibles en filter pills
 *  - Exclut les labels d'autres outils (`[Superhuman]`, `[Notion]/...`, etc.)
 *  - Résolution colors via labelsState.byId
 *
 * Pas de cache mémoïsé — appelé pour chaque row à chaque render, mais
 * le coût (Set + lookup) est négligeable vs le rendering Svelte.
 */
export function getDisplayLabels(messages: GmailMessage[]): GmailLabel[] {
  if (labelsState.byId.size === 0) return [];
  const ids = new Set<string>();
  for (const m of messages) {
    for (const id of m.labelIds ?? []) ids.add(id);
  }
  const out: GmailLabel[] = [];
  for (const id of ids) {
    const lbl = labelsState.byId.get(id);
    if (!lbl) continue;
    if (lbl.type !== "user") continue;
    if (isSplitName(lbl.name)) continue;
    if (isBracketPrefixed(lbl.name)) continue; // [Superhuman], [Notion]/IA, etc.
    out.push(lbl);
  }
  // Tri alphabétique pour stabilité d'affichage
  out.sort((a, b) => a.name.localeCompare(b.name));
  return out;
}
